import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../api/axiosConfig';
import './VisitDetailPage.css';

// Groupes de marques pour le Relevé de Stock (Eaux / Boissons Gazeuses / Boissons Énergisantes)
const GROUPES_STOCK = [
  { titre: 'EAUX', marques: ['Supermont', 'Opur', 'VITAL', 'TANGUI', 'MADIBA', 'CEILO', 'SANO', 'AQUABELLE', 'ULTIME LIGHT', 'VALCLAIR', 'AUTRES EAUX'] },
  { titre: 'BOISSONS GAZEUSES', marques: ['BG SP', 'BG BC', 'BG ELIM', 'BG GRACEDOM', 'BG UCB', 'BG BRASAF', 'BG ULTIMATE', 'AUTRES BG'] },
  { titre: 'BOISSONS ÉNERGISANTES', marques: ['ED SP', 'ED BC', 'ED ELIM', 'ED UCB', 'ED ULTIMATE', 'AUTRES ED'] },
];

// Liste plate de toutes les marques SP (utilisée par splitStockEtVeille)
const MARQUES_STOCK_SP = GROUPES_STOCK.flatMap(g => g.marques);

// Sépare une liste veilles_concurrentielles en {stocks, veilles} selon la logique du formulaire mobile
const splitStockEtVeille = (rows) => {
  const stocks = [];
  const veilles = [];
  (rows || []).forEach(row => {
    const marqueSP = row.marque && MARQUES_STOCK_SP.includes(row.marque);
    if (marqueSP && (row.nombre_packs || 0) > 0) {
      stocks.push(row);
    }
    const hasVeilleData = row.concurrent_id || row.concurrent?.nom || row.activite_observee
      || row.mecanisme || row.observation || row.pretexte || (!marqueSP && row.marque);
    if (hasVeilleData) {
      veilles.push(row);
    }
  });
  return { stocks, veilles };
};

function VisitDetailPage() {
  const { visiteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [visite, setVisite] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Déterminer la page de retour en fonction de l'URL actuelle
  // Revenir à la page d'où l'on vient (à valider, historique, rejetés...) ; si la page
  // a été ouverte directement par un lien, il n'y a pas de précédent : on retombe sur la
  // liste par défaut du rôle.
  const goBackToList = () => {
    if (location.key !== 'default') {
      navigate(-1);
    } else {
      navigate(getBackPath());
    }
  };

  const getBackPath = () => {
    if (location.pathname.includes('/chef-zone/')) {
      return '/chef-zone/validations';
    } else if (location.pathname.includes('/responsable/')) {
      return '/responsable/validations';
    } else if (location.pathname.includes('/superviseur-en-chef/')) {
      return '/superviseur-en-chef/validations';
    } else if (location.pathname.includes('/admin/')) {
      return '/admin/reports';
    }
    return '/dashboard';
  };

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        console.log('Chargement des détails de la visite:', visiteId);
        const response = await axiosInstance.get(`/visites/${visiteId}`);
        console.log('Données reçues:', response.data);
        setVisite(response.data);
      } catch (error) {
        console.error("Erreur de chargement des détails:", error);
        if (error.response) {
          console.error("Status:", error.response.status);
          console.error("Data:", error.response.data);
        }
        alert(`Erreur de chargement: ${error.response?.data?.detail || error.message}`);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetails();
  }, [visiteId]);

  const handleAction = async (action) => {
    const endpoint = action === 'valider' ? `/visites/${visiteId}/valider` : `/visites/${visiteId}/rejeter`;

    // Si c'est un rejet, demander la raison
    let commentaire = null;
    if (action === 'rejeter') {
      commentaire = window.prompt(
        'Veuillez indiquer la raison du rejet de cette visite :\n\n(Cette information sera envoyée au merchandiser)',
        ''
      );

      // Si l'utilisateur annule ou ne fournit pas de raison
      if (commentaire === null) {
        return; // Annuler l'action
      }

      // Trim le commentaire
      commentaire = commentaire.trim();

      // Demander confirmation si pas de raison fournie
      if (commentaire === '') {
        const confirmer = window.confirm(
          'Vous n\'avez pas indiqué de raison. Voulez-vous vraiment rejeter sans raison ?'
        );
        if (!confirmer) {
          return; // Annuler l'action
        }
      }
    }

    try {
      // Envoyer la requête avec le commentaire si c'est un rejet
      if (action === 'rejeter') {
        await axiosInstance.put(endpoint, { commentaire });
      } else {
        await axiosInstance.put(endpoint);
      }

      alert(`Rapport ${action === 'valider' ? 'validé' : 'rejeté'} avec succès !`);
      navigate('/dashboard');
    } catch (error) {
      console.error('Erreur lors de l\'action:', error);
      alert(`Erreur lors de l'action : ${action}`);
    }
  };

  if (isLoading) return <div className="loading">Chargement des détails...</div>;
  if (!visite) return <div className="error">Impossible de charger les détails de la visite.</div>;

  return (
    <div className="visit-detail-page">
      <header className="detail-header">
        <div>
          <h1>Rapport de Visite #{visite.id}</h1>
          <p>
            <strong>Date :</strong> {new Date(visite.date_visite).toLocaleDateString()} |
            <strong>Statut :</strong> <span className={`status-badge ${visite.statut_validation}`}>{visite.statut_validation}</span>
          </p>
        </div>
         <button onClick={goBackToList} className="back-button">
          Retour à la liste
        </button>
      </header>

      {/* Informations Acteurs */}
      <section className="detail-section actors-section">
        <h2>👥 Acteurs de la Visite</h2>
        <div className="detail-grid">
          <div className="detail-item">
            <strong>Merchandiser :</strong> {visite.merchandiser?.user?.nom || 'N/A'}
          </div>
          <div className="detail-item">
            <strong>Zone :</strong> {(visite.client?.est_gms ? visite.client?.chef_zone?.zone : visite.merchandiser?.chef_zone?.zone) || 'N/A'}
          </div>
          <div className="detail-item">
            <strong>Chef de Zone :</strong> {(visite.client?.est_gms ? visite.client?.chef_zone?.user?.nom : visite.merchandiser?.chef_zone?.user?.nom) || 'N/A'}
          </div>
          {visite.client?.est_gms && visite.client?.chef_zone?.responsable && (
            <div className="detail-item">
              <strong>Responsable GMS :</strong> {visite.client?.chef_zone?.responsable?.user?.nom || 'N/A'}
            </div>
          )}
          <div className="detail-item">
            <strong>Commercial :</strong> {visite.client?.commercial?.nom || visite.client?.commercial_nom || 'N/A'}
          </div>
        </div>
      </section>

      {/* Informations Client Enrichies */}
      <section className="detail-section client-section">
        <h2>🏪 Informations Client</h2>
        <div className="detail-grid">
          <div className="detail-item">
            <strong>Nom Client :</strong> {visite.client?.nom_client || 'N/A'}
          </div>
          <div className="detail-item">
            <strong>Zone :</strong> {visite.client?.zone || 'N/A'}
          </div>
          <div className="detail-item">
            <strong>Commercial :</strong> {visite.client?.commercial?.nom || visite.client?.commercial_nom || 'N/A'}
          </div>
          <div className="detail-item">
            <strong>Lieu-dit :</strong> {visite.client?.lieu_dit || 'N/A'}
          </div>
          <div className="detail-item">
            <strong>Contact :</strong> {visite.client?.contact || 'N/A'}
          </div>
          <div className="detail-item">
            <strong>Typologie :</strong> {visite.client?.typologie || 'N/A'}
          </div>
          <div className="detail-item">
            <strong>Localisation :</strong> {visite.client?.localisation || 'N/A'}
          </div>
        </div>
      </section>
      
      <div className="form-content">
        {/* Géolocalisation - Vérification présence sur site */}
        <section className="detail-section geolocation-section">
          <h2>📍 Vérification Terrain</h2>
          <div className="geolocation-status">
            {visite.est_sur_site !== null && visite.est_sur_site !== undefined ? (
              <div className={`geo-badge ${visite.est_sur_site ? 'on-site' : 'off-site'}`}>
                {visite.est_sur_site ? '✓ SUR SITE' : '✗ HORS SITE'}
              </div>
            ) : (
              <div className="geo-badge no-gps">
                Position non vérifiée
              </div>
            )}
          </div>
          <div className="detail-grid">
            <div className="detail-item">
              <strong>Distance :</strong> {visite.distance_client !== null && visite.distance_client !== undefined ? `${visite.distance_client} m` : 'N/A'}
            </div>
            <div className="detail-item">
              <strong>Précision :</strong> {visite.precision_gps ? `± ${Math.round(visite.precision_gps)} m` : 'N/A'}
            </div>
          </div>

          <div className="detail-item">
            <p><strong>Position de soumission du rapport</strong></p>
            {/* On vérifie que les coordonnées existent avant d'afficher le lien */}
            {visite.latitude_soumission && visite.longitude_soumission ? (
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${visite.latitude_soumission},${visite.longitude_soumission}`} 
                target="_blank" // Ouvre dans un nouvel onglet
                rel="noopener noreferrer" // Bonne pratique de sécurité
                className="map-link-inline"
              >
                  Voir la position sur Google Maps
              </a>
            ) : (
              <span style={{ color: '#888' }}>Position non enregistrée pour cette visite.</span>
            )}
          </div>


        </section>

        {/* Informations Générales */}
        <section className="detail-section">
          <h2>📋 Informations Générales</h2>
          <div className="detail-grid">
            <div className="detail-item"><strong>Base :</strong> {visite.base || 'N/A'}</div>
            <div className="detail-item"><strong>Heure Début :</strong> {visite.heure_debut || 'N/A'}</div>
            <div className="detail-item"><strong>Heure Fin :</strong> {visite.heure_fin || 'N/A'}</div>
            <div className="detail-item"><strong>Réseau Distribution :</strong> {visite.reseau_distribution || 'N/A'}</div>
            <div className="detail-item"><strong>Type Client :</strong> {visite.type_client || 'N/A'}</div>
            {visite.client_direct_nom && (
              <div className="detail-item"><strong>Client Direct :</strong> {visite.client_direct_nom}</div>
            )}
          </div>
        </section>

        {/* Équipements/Outils */}
        <section className="detail-section">
          <h2>🛠️ Équipements/Outils</h2>
          {(() => {
            // Parser les équipements depuis les champs concaténés
            const types = visite.type_outil ? visite.type_outil.split(';') : [];
            const marques = visite.marque_outil ? visite.marque_outil.split(';') : [];
            const etats = visite.etat_outil ? visite.etat_outil.split(';') : [];
            const actions = visite.action_outil ? visite.action_outil.split(';') : [];
            const details = visite.details_outil ? visite.details_outil.split(';') : [];
            const photosEquipement = visite.photos_equipement || [];

            // Trier les photos par equipment_index pour afficher dans le bon ordre
            const photosTriees = [...photosEquipement].sort((a, b) => {
              const indexA = a.equipment_index ?? 0;
              const indexB = b.equipment_index ?? 0;
              return indexA - indexB;
            });

            // Créer un tableau d'équipements
            const maxLength = Math.max(types.length, marques.length, etats.length);

            if (maxLength === 0) {
              return <p className="no-data-message">Aucun équipement signalé</p>;
            }

            return (
              <div className="veille-grid">
                {Array.from({ length: maxLength }).map((_, index) => {
                  // Trouver la photo correspondante par index
                  const photo = photosTriees.find(p => p.equipment_index === index);

                  return (
                    <div key={`equipment-${index}`} className="equipment-card">
                      <div className="equipment-info">
                        <div className="veille-info">
                          <span className="veille-label">Type:</span>
                          <span className="veille-value">{types[index] || 'N/A'}</span>
                        </div>
                        <div className="veille-info">
                          <span className="veille-label">Marque:</span>
                          <span className="veille-value">{marques[index] || 'N/A'}</span>
                        </div>
                        <div className="veille-info">
                          <span className="veille-label">État:</span>
                          <span className="veille-value">{etats[index] || 'N/A'}</span>
                        </div>
                        {actions[index] && actions[index] !== 'AUCUNE' && (
                          <div className="veille-info">
                            <span className="veille-label">Action:</span>
                            <span className="veille-value" style={{ color: '#e67e22', fontWeight: '600' }}>{actions[index]}</span>
                          </div>
                        )}
                        {details[index] && details[index].trim() && (
                          <div className="veille-info">
                            <span className="veille-label">Détails:</span>
                            <span className="veille-value">{details[index]}</span>
                          </div>
                        )}
                      </div>
                      {photo && (
                        <div className="equipment-photo">
                          <a href={`https://spmerchflow.com:8000${photo.photo_url}`} target="_blank" rel="noopener noreferrer">
                            <img
                              src={`https://spmerchflow.com:8000${photo.photo_url}`}
                              alt={`Équipement ${types[index] || index + 1}`}
                              className="equipment-photo-img"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/150x150?text=Image+non+disponible';
                              }}
                            />
                          </a>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </section>

        {/* Conformité */}
        <section className="detail-section">
          <h2>✅ Conformité</h2>
          <div className="detail-grid">
            <div className="detail-item"><strong>FEFO respecté :</strong> {visite.fefo_respecte ? '✓ Oui' : '✗ Non'}</div>
            <div className="detail-item"><strong>Frigo SP présent :</strong> {visite.a_frigo_sp === true ? '✓ Oui' : visite.a_frigo_sp === false ? '✗ Non' : '—'}</div>
            {visite.a_frigo_sp === true && (
              <div className="detail-item"><strong>Planogramme conforme :</strong> {visite.planogramme_conforme === true ? '✓ Oui' : visite.planogramme_conforme === false ? '✗ Non' : '—'}</div>
            )}
            {visite.a_frigo_sp === false && (
              <div className="detail-item"><strong>PDV mérite un frigo SP :</strong> {visite.pdv_merite_frigo === true ? '✓ Oui' : visite.pdv_merite_frigo === false ? '✗ Non' : '—'}</div>
            )}
          </div>
          {visite.a_frigo_sp === true && visite.planogramme_conforme === false && visite.non_conformites_planogramme?.length > 0 && (
            <div className="detail-item" style={{ marginTop: 8 }}>
              <strong>Raison non-conformité :</strong> {visite.non_conformites_planogramme.join(', ')}
            </div>
          )}
          {visite.observation_planogramme && (
            <div className="detail-item">
              <p><strong>Observation Planogramme :</strong></p>
              <p className="observation-text">{visite.observation_planogramme}</p>
            </div>
          )}
        </section>

        {/* Ruptures de Stock */}
        <section className="detail-section">
          <h2>📦 Ruptures de Stock</h2>
          <div className="detail-item">
            {visite.ruptures_visite?.length > 0 ? (
              <p className="observation-text">{visite.ruptures_visite.map(r => r.produit?.nom_produit || r.produit_id).join(', ')}</p>
            ) : visite.ruptures ? (
              <p className="observation-text">{visite.ruptures}</p>
            ) : (
              <p className="no-data-message">Aucune rupture signalée</p>
            )}
          </div>
        </section>

        {/* Incidents */}
        <section className="detail-section">
          <h2>⚠️ Incidents</h2>
          {(() => {
            const incidents = visite.details_produits?.filter(d => d.type_detail === 'incident') || [];
            const photosIncident = visite.photos_incident || [];

            if (incidents.length > 0) {
              return (
                <div className="veille-grid">
                  {incidents.map((incident, incidentIndex) => {
                    // Utiliser incident_key pour regrouper les photos
                    const incidentPhotos = incident.incident_key
                      ? photosIncident
                          .filter(photo => photo.incident_key === incident.incident_key)
                          .sort((a, b) => a.photo_index - b.photo_index)
                      : [];

                    return (
                      <div key={`incident-${incident.id || incidentIndex}`} className="incident-card">
                        <div className="incident-info">
                          <div className="veille-header">
                            <strong>{incident.produit?.article || incident.produit?.nom_produit || 'Article inconnu'}</strong>
                          </div>
                          <div className="veille-body">
                            <div className="veille-info">
                              <span className="veille-label">Type d'incident:</span>
                              <span className="veille-value">{incident.observation || visite.type_incidents || 'N/A'}</span>
                            </div>
                            {incident.observation_detaillee && (
                              <div className="veille-info">
                                <span className="veille-label">Observation détaillée:</span>
                                <span className="veille-value">{incident.observation_detaillee}</span>
                              </div>
                            )}
                            <div className="veille-info">
                              <span className="veille-label">Quantité:</span>
                              <span className="veille-value">{incident.quantite || 0}</span>
                            </div>
                          </div>
                        </div>
                        {incidentPhotos.length > 0 && (
                          <div className="incident-photos">
                            <p className="incident-photos-label">Photo:</p>
                            <a
                              href={`https://spmerchflow.com:8000${incidentPhotos[0].photo_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="incident-photo-link"
                            >
                              <img
                                src={`https://spmerchflow.com:8000${incidentPhotos[0].photo_url}`}
                                alt={`${incident.produit?.article || incident.produit?.nom_produit || 'Incident'}`}
                                className="incident-photo-img"
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/100x100?text=Image+non+disponible';
                                }}
                              />
                            </a>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            } else if (visite.type_incidents || visite.articles_incidents) {
              // Fallback sur les champs récapitulatifs si pas de détails
              return (
                <div className="detail-grid">
                  <div className="detail-item"><strong>Type :</strong> {visite.type_incidents || 'N/A'}</div>
                  <div className="detail-item"><strong>Articles :</strong> {visite.articles_incidents || 'N/A'}</div>
                  <div className="detail-item"><strong>Quantité :</strong> {visite.quantite_incidents || 'N/A'}</div>
                </div>
              );
            } else {
              return <p className="no-data-message">Aucun incident signalé</p>;
            }
          })()}
        </section>

        {/* Relevé de Stock + Veille Concurrentielle */}
        {(() => {
          const { stocks: stocksLegacy, veilles } = splitStockEtVeille(visite.veilles_concurrentielles);
          const releveStocks = visite.releves_stock || [];
          return (
            <>
              {/* Relevé de Stock — priorité releves_stock, fallback veilles_concurrentielles */}
              <section className="detail-section">
                <h2>📦 Relevé de Stock</h2>
                {releveStocks.length > 0 ? (() => {
                  // La catégorie enregistrée en base fait foi (1 = eaux, 2 = BG, 3 = ED).
                  // On ne retombe sur le nom que si le produit n'a pas de catégorie.
                  const classifierProduit = (produit) => {
                    const parCategorie = { 1: 'EAU', 2: 'BG', 3: 'ED' }[produit?.categorie_id];
                    if (parCategorie) return parCategorie;
                    const n = (produit?.nom_produit || '').toUpperCase();
                    if (n.startsWith('ED ') || n.includes('REAKTOR')) return 'ED';
                    if (n.startsWith('BG ') || n.includes('PLANET') || n.includes('BUBBLE') || n.includes('AMERICAN') || n.includes('COLA')) return 'BG';
                    return 'EAU';
                  };
                  const FAMILLES_RELEVE = [
                    { titre: 'EAUX', code: 'EAU' },
                    { titre: 'BOISSONS GAZEUSES', code: 'BG' },
                    { titre: 'BOISSONS ÉNERGISANTES', code: 'ED' },
                  ];
                  return (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#007bff', color: 'white' }}>
                            <th style={{ padding: '10px', textAlign: 'left', borderRight: '1px solid #cfe2ff' }}>PRODUIT</th>
                            <th style={{ padding: '10px', textAlign: 'center' }}>QUANTITÉ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {FAMILLES_RELEVE.map(famille => {
                            const items = releveStocks.filter(r => classifierProduit(r.produit) === famille.code);
                            if (items.length === 0) return null;
                            const total = items.reduce((sum, r) => sum + (parseInt(r.quantite_en_stock) || 0), 0);
                            return (
                              <React.Fragment key={famille.titre}>
                                <tr style={{ backgroundColor: '#cfe2ff' }}>
                                  <td style={{ padding: '8px 10px', fontWeight: 'bold', color: '#0d3c7d', borderRight: '1px solid #b6d4fe' }}>{famille.titre}</td>
                                  <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 'bold', color: '#0d3c7d' }}>Total : {total}</td>
                                </tr>
                                {items.map((releve, index) => (
                                  <tr key={`releve-${releve.id || index}`} style={{ borderBottom: '1px solid #e3edf7' }}>
                                    <td style={{ padding: '8px 10px', paddingLeft: '24px', fontWeight: '600', color: '#0d3c7d', backgroundColor: '#eaf2fb', borderRight: '1px solid #cfe2ff' }}>
                                      {releve.produit?.nom_produit || releve.produit?.article || `Produit #${releve.produit_id}`}
                                    </td>
                                    <td style={{ padding: '8px 10px', textAlign: 'center', color: '#0d3c7d', fontWeight: '600' }}>
                                      {releve.quantite_en_stock}
                                    </td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })() : stocksLegacy.length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#007bff', color: 'white' }}>
                          <th style={{ padding: '10px', textAlign: 'left', borderRight: '1px solid #cfe2ff' }}>MARQUE</th>
                          <th style={{ padding: '10px', textAlign: 'center' }}>PACKS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {GROUPES_STOCK.map(groupe => {
                          const items = stocksLegacy.filter(s => groupe.marques.includes(s.marque));
                          if (items.length === 0) return null;
                          const totalPacks = items.reduce((sum, it) => sum + (parseInt(it.nombre_packs) || 0), 0);
                          return (
                            <React.Fragment key={groupe.titre}>
                              <tr style={{ backgroundColor: '#cfe2ff' }}>
                                <td style={{ padding: '8px 10px', fontWeight: 'bold', color: '#0d3c7d', borderRight: '1px solid #b6d4fe' }}>{groupe.titre}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 'bold', color: '#0d3c7d' }}>Total : {totalPacks}</td>
                              </tr>
                              {items.map((item, index) => (
                                <tr key={`stock-${item.id || `${groupe.titre}-${index}`}`} style={{ borderBottom: '1px solid #e3edf7' }}>
                                  <td style={{ padding: '8px 10px', paddingLeft: '24px', fontWeight: '600', color: '#0d3c7d', backgroundColor: '#eaf2fb', borderRight: '1px solid #cfe2ff' }}>{item.marque}</td>
                                  <td style={{ padding: '8px 10px', textAlign: 'center', color: '#0d3c7d', fontWeight: '600' }}>{item.nombre_packs}</td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : <p className="no-data-message">Aucun relevé de stock pour cette visite.</p>}
              </section>

              {/* Veille Concurrentielle */}
              <section className="detail-section">
                <h2>🔍 Veille Concurrentielle</h2>
                {veilles.length > 0 ? (
                  <div className="veille-grid">
                    {veilles.map((item, index) => {
                      const concurrentNom = item.concurrent?.nom || item.marque || 'Concurrent inconnu';
                      return (
                        <div key={`veille-${item.id || index}`} className="veille-card">
                          <div className="veille-header">
                            <strong style={{ color: '#c0392b' }}>⚔️ {concurrentNom}</strong>
                          </div>
                          <div className="veille-body">
                            {item.activite_observee && (
                              <div className="veille-info">
                                <span className="veille-label">Activité:</span>
                                <span className="veille-value">{item.activite_observee}</span>
                              </div>
                            )}
                            {item.produits_concernes && (
                              <div className="veille-info">
                                <span className="veille-label">Produits concernés:</span>
                                <span className="veille-value">{item.produits_concernes}</span>
                              </div>
                            )}
                            {item.mecanisme && (
                              <div className="veille-info">
                                <span className="veille-label">Mécanisme:</span>
                                <span className="veille-value">{item.mecanisme}</span>
                              </div>
                            )}
                            {item.observation && (
                              <div className="veille-info">
                                <span className="veille-label">Observation:</span>
                                <span className="veille-value">{item.observation}</span>
                              </div>
                            )}
                            {item.pretexte && (
                              <div className="veille-info">
                                <span className="veille-label">Prétexte:</span>
                                <span className="veille-value">{item.pretexte}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : <p className="no-data-message">Aucune observation de veille pour cette visite.</p>}
              </section>
            </>
          );
        })()}

        {/* Veille de Prix */}
        <section className="detail-section">
          <h2>💰 Veille de Prix Concurrents</h2>
          {visite.veilles_prix?.length > 0 ? (() => {
            // Classification alignée sur la structure mobile (GROUPES_VEILLE_PRIX)
            const MARQUES_EAUX = ['TANGUI','VITALE','ULTIME LIGHT','CIELO','CEILO','MADIBA','SUPERMONT','OPUR','VALCAIR','VALCLAIR','AQUA LIFE'];
            const MARQUES_BG = ['SPECIAL','RAZZL','COCACOLA','COCA COLA','COCA-COLA','FANTA','SPRITE','SHWEPESS','SCHWEPPES','TOP','WORLD COLA','YOUZOU','DJINO','VIMTO','ORANGINA',"C'FRUT",'CFRUT','KRYSTAL','SOURCE DU PAYS','BIG COLA'];
            const MARQUES_ED = ['REAKTOR','KIQ','XXL','VOLT','ULTIME ENERGY','FEROX','FEROX CANETTE'];
            const categorize = (marque) => {
              const m = (marque || '').toUpperCase().trim();
              if (MARQUES_EAUX.includes(m)) return 'EAUX';
              if (MARQUES_BG.includes(m)) return 'BG';
              if (MARQUES_ED.includes(m)) return 'ED';
              return 'AUTRES';
            };
            const groups = { EAUX: [], BG: [], ED: [], AUTRES: [] };
            visite.veilles_prix.forEach(item => { groups[categorize(item.marque)].push(item); });
            const ordre = [
              { key: 'EAUX', label: '💧 Eaux', color: '#1565c0' },
              { key: 'BG', label: '🥤 Boissons Gazeuses (BG)', color: '#bf6f00' },
              { key: 'ED', label: '⚡ Énergisantes (ED)', color: '#7b1fa2' },
              { key: 'AUTRES', label: '❓ Autres', color: '#616161' },
            ];
            return (
              <div>
                {ordre.filter(g => groups[g.key].length > 0).map(g => (
                  <div key={g.key} style={{ marginBottom: '20px' }}>
                    <h3 style={{ color: g.color, marginBottom: '10px', borderBottom: `2px solid ${g.color}`, paddingBottom: '5px' }}>
                      {g.label} <span style={{ fontSize: '13px', color: '#666', fontWeight: 'normal' }}>({groups[g.key].length})</span>
                    </h3>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <thead>
                          <tr style={{ backgroundColor: g.color, color: 'white' }}>
                            <th style={{ padding: '10px', textAlign: 'left', borderRight: '1px solid #ddd' }}>MARQUE</th>
                            <th style={{ padding: '10px', textAlign: 'center', borderRight: '1px solid #ddd' }}>TAILLE</th>
                            <th style={{ padding: '10px', textAlign: 'center', borderRight: '1px solid #ddd' }}>BTL/PACK</th>
                            <th style={{ padding: '10px', textAlign: 'center', borderRight: '1px solid #ddd', backgroundColor: '#2e7d32' }}>GROS. ACHAT</th>
                            <th style={{ padding: '10px', textAlign: 'center', borderRight: '1px solid #ddd', backgroundColor: '#2e7d32' }}>GROS. VENTE</th>
                            <th style={{ padding: '10px', textAlign: 'center', borderRight: '1px solid #ddd', backgroundColor: '#2e7d32' }}>GMS ACHAT</th>
                            <th style={{ padding: '10px', textAlign: 'center', borderRight: '1px solid #ddd', backgroundColor: '#2e7d32' }}>GMS VENTE</th>
                            <th style={{ padding: '10px', textAlign: 'center', backgroundColor: '#2e7d32' }}>PRIX CONSO</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groups[g.key].map((item, index) => (
                            <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'white', borderBottom: '1px solid #e0e0e0' }}>
                              <td style={{ padding: '8px 10px', fontWeight: '600', color: '#0d3c7d', backgroundColor: '#dbeafe', borderRight: '1px solid #ddd' }}>{item.marque}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'center', color: '#555', backgroundColor: '#dbeafe', borderRight: '1px solid #ddd' }}>{item.taille_bouteille || '—'}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'center', color: '#555', backgroundColor: '#dbeafe', borderRight: '1px solid #ddd' }}>{item.bouteilles_pack != null ? item.bouteilles_pack : '—'}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'center', borderRight: '1px solid #ddd' }}>{item.prix_grossiste_achat != null ? `${item.prix_grossiste_achat} F` : '—'}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'center', borderRight: '1px solid #ddd' }}>{item.prix_grossiste_vente != null ? `${item.prix_grossiste_vente} F` : '—'}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'center', borderRight: '1px solid #ddd' }}>{item.prix_gms_achat != null ? `${item.prix_gms_achat} F` : '—'}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'center', borderRight: '1px solid #ddd' }}>{item.prix_gms_vente != null ? `${item.prix_gms_vente} F` : '—'}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'center' }}>{item.prix_conso != null ? `${item.prix_conso} F` : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            );
          })() : <p className="no-data-message">Aucune veille de prix pour cette visite.</p>}
        </section>

        {/* Observations Générales */}
        {visite.observations_generales && (
          <section className="detail-section">
            <h2>📝 Observations Générales</h2>
            <div className="detail-item">
              <p className="observation-text">{visite.observations_generales}</p>
            </div>
          </section>
        )}

        {/* Photos Rayon */}
        {visite.photos_rayon && visite.photos_rayon.length > 0 && (
          <section className="detail-section">
            <h2>📸 Photos Rayon (Achalandage)</h2>
            <div className="photos-rayon-grid">
              {['Rayon Froid', 'Rayon Ordinaire'].map(typeRayon => {
                const photosType = visite.photos_rayon.filter(p => p.type_rayon === typeRayon);
                if (photosType.length === 0) return null;

                return (
                  <div key={typeRayon} className="photos-rayon-type">
                    <h3>{typeRayon === 'Rayon Froid' ? '❄️ Rayon Froid' : '🌡️ Rayon Ordinaire'}</h3>
                    <div className="photos-moment-container">
                      {['AVANT', 'APRES'].map(moment => {
                        const photosByMoment = photosType.filter(p => p.moment === moment);
                        return (
                          <div key={moment} className="photo-moment-section">
                            <p className="photo-moment-label">
                              {moment} {photosByMoment.length > 1 && `(${photosByMoment.length} photos)`}
                            </p>
                            {photosByMoment.length > 0 ? (
                              <div className="photos-grid">
                                {photosByMoment.map((photo, index) => (
                                  <a
                                    key={`${photo.id}-${index}`}
                                    href={`https://spmerchflow.com:8000${photo.photo_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="photo-item"
                                  >
                                    <img
                                      src={`https://spmerchflow.com:8000${photo.photo_url}`}
                                      alt={`${typeRayon} ${moment} ${index + 1}`}
                                      className="photo-preview"
                                      onError={(e) => {
                                        e.target.src = 'https://via.placeholder.com/300x200?text=Image+non+disponible';
                                      }}
                                    />
                                    <span className="photo-number">#{index + 1}</span>
                                  </a>
                                ))}
                              </div>
                            ) : (
                              <div className="photo-placeholder">Aucune photo</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}


        {/* Section Actions - Affichée uniquement si le rapport est en attente */}
        {visite.statut_validation === 'soumis' && (
          <section className="actions-section">
            <button onClick={() => handleAction('valider')} className="action-button approve-button">Valider le Rapport</button>
            <button onClick={() => handleAction('rejeter')} className="action-button reject-button">Rejeter le Rapport</button>
          </section>
        )}

        {/* Message de statut - Affiché lorsque le rapport est validé ou rejeté */}
        {visite.statut_validation !== 'soumis' && (
          <section className="status-section">
            <div className={`status-message ${visite.statut_validation}`}>
              {visite.statut_validation === 'valide' && (
                <>
                  <span className="status-icon">✓</span>
                  <p>Rapport validé le {visite.date_validation ? new Date(visite.date_validation).toLocaleDateString() : 'N/A'}</p>
                  {visite.validateur && <p className="validator-info">Par: {visite.validateur.nom || 'N/A'}</p>}
                </>
              )}
              {visite.statut_validation === 'rejete' && (
                <>
                  <span className="status-icon">✗</span>
                  <p>Rapport rejeté le {visite.date_validation ? new Date(visite.date_validation).toLocaleDateString() : 'N/A'}</p>
                  {visite.validateur && <p className="validator-info">Par: {visite.validateur.nom || 'N/A'}</p>}
                </>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default VisitDetailPage;
